import React from 'react';
import { Input } from '@/components/ui/input';
import { NumberField } from '@/components/ui/number-field';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PolicySeverity = 'low' | 'medium' | 'high';
export type PolicyEnforcement = 'warn' | 'block';
export type MonitoringFrequency = 'day' | 'week';

export interface PolicyCheckConfig {
  enabled: boolean;
  threshold: number;
  severity: PolicySeverity;
  enforcement: PolicyEnforcement;
}

export interface PolicyCheckCardProps {
  title: string;
  config: PolicyCheckConfig;
  onChange: (config: PolicyCheckConfig) => void;
  monitoringFrequency: MonitoringFrequency;
  expectedHoursPerWeek: number;
  dailyHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  isMissingHours?: boolean;
  isNonWorkingDay?: boolean;
  isLegalMaximum?: boolean;
}

const getEnforcementBorderStyle = (enforcement: PolicyEnforcement, severity: PolicySeverity, enabled: boolean): { className: string; color: string } => {
  if (!enabled) return { className: 'border-l-muted-foreground/30', color: '' };
  if (enforcement === 'block' || severity === 'high') return { className: 'border-l-red-500 dark:border-l-red-400', color: 'var(--color-red-500)' };
  if (severity === 'low') return { className: 'border-l-muted-foreground/50', color: '' };
  return { className: 'border-l-amber-400 dark:border-l-amber-500', color: 'var(--color-amber-400)' };
};

export const PolicyCheckCard: React.FC<PolicyCheckCardProps> = ({
  title,
  config,
  onChange,
  monitoringFrequency,
  expectedHoursPerWeek,
  dailyHours,
  isMissingHours = false,
  isNonWorkingDay = false,
  isLegalMaximum = false,
}) => {
  const handleToggle = (checked: boolean) => {
    onChange({ ...config, enabled: checked });
  };

  const handleThresholdChange = (value: number) => {
    onChange({ ...config, threshold: value });
  };

  const handleSeverityChange = (value: PolicySeverity) => {
    onChange({ ...config, severity: value });
  };

  const handleEnforcementChange = (value: PolicyEnforcement) => {
    onChange({ ...config, enforcement: value });
  };

  const borderStyle = getEnforcementBorderStyle(config.enforcement, config.severity, config.enabled);

  // ── Field 1: "Expected / Trigger" label + value ──────────────────────────
  const field1Label = isLegalMaximum
    ? 'Maximum allowed'
    : isNonWorkingDay
      ? 'Trigger'
      : monitoringFrequency === 'week'
        ? 'Expected per week'
        : 'Expected per day';

  const field1Content = isLegalMaximum ? (
    <div className="flex items-center gap-1.5">
      <NumberField
        value={config.threshold}
        onChange={handleThresholdChange}
        disabled={!config.enabled}
        step={0.5}
        min={0}
        className="h-8 text-sm"
      />
      <span className="text-xs text-muted-foreground whitespace-nowrap">h / {monitoringFrequency}</span>
    </div>
  ) : (
    <Input
      type="text"
      value={
        isNonWorkingDay
          ? 'Non-working day entry'
          : monitoringFrequency === 'week'
            ? `${expectedHoursPerWeek}h (working profile)`
            : 'Per day (working profile)'
      }
      disabled
      className="h-8 text-sm bg-muted/50 cursor-not-allowed"
    />
  );

  // ── Field 2: "Alert when over/under by" (hidden for non-working & legal) ─
  const showThresholdField = !isNonWorkingDay && !isLegalMaximum;

  const field2Content = showThresholdField ? (
    <div className="flex items-center gap-1.5">
      <NumberField
        value={config.threshold}
        onChange={handleThresholdChange}
        disabled={!config.enabled}
        step={0.5}
        min={0}
        className="h-8 text-sm"
      />
      <span className="text-xs text-muted-foreground">h</span>
    </div>
  ) : (
    <Input
      type="text"
      value="N/A"
      disabled
      className="h-8 text-sm bg-muted/50 cursor-not-allowed"
    />
  );

  return (
    <TooltipProvider>
      <div
        className={cn(
          'border rounded-lg border-l-4 transition-all duration-200 bg-background',
          borderStyle.className,
          !config.enabled && 'opacity-60 bg-muted/20',
        )}
        style={borderStyle.color ? { borderLeftColor: borderStyle.color } : undefined}
      >
        {/* ── Row: checkbox + title + 4 fields ─────────────────────────────── */}
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            aria-label={`Enable ${title}`}
            className="h-4 w-4 flex-shrink-0 rounded border-gray-300"
          />

          {/* Title */}
          <span className={cn('text-sm font-semibold flex-shrink-0 w-44', !config.enabled && 'text-muted-foreground')}>
            {title}
          </span>

          {/* 4-column fields grid */}
          <div className="flex-1 grid grid-cols-4 gap-3 items-end">
            {/* Field 1 – Expected / Maximum */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{field1Label}</Label>
              {field1Content}
            </div>

            {/* Field 2 – Alert threshold */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {showThresholdField
                  ? (isMissingHours ? 'Alert when under by' : 'Alert when over by')
                  : 'Threshold'}
              </Label>
              {field2Content}
            </div>

            {/* Field 3 – Severity */}
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <Label className="text-xs text-muted-foreground">Severity</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[260px]">
                    <p>
                      {isLegalMaximum
                        ? 'Legal maximum violations are always Critical.'
                        : 'Controls the visual priority of this alert in Insights.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {isLegalMaximum ? (
                <Input
                  type="text"
                  value="High (fixed)"
                  disabled
                  className="h-8 text-sm bg-muted/50 cursor-not-allowed"
                />
              ) : (
                <select
                  value={config.severity}
                  onChange={(e) => handleSeverityChange(e.target.value as PolicySeverity)}
                  disabled={!config.enabled}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              )}
            </div>

            {/* Field 4 – Enforcement */}
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <Label className="text-xs text-muted-foreground">Enforcement</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[260px]">
                    <p>
                      {isLegalMaximum
                        ? 'Legal maximum violations always block timesheet submission.'
                        : 'Warn only shows the alert but allows submission. Block prevents submission until resolved.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {isLegalMaximum ? (
                <Input
                  type="text"
                  value="Block (fixed)"
                  disabled
                  className="h-8 text-sm bg-muted/50 cursor-not-allowed"
                />
              ) : (
                <select
                  value={config.enforcement}
                  onChange={(e) => handleEnforcementChange(e.target.value as PolicyEnforcement)}
                  disabled={!config.enabled}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="warn">Warn only</option>
                  <option value="block">Block submission</option>
                </select>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
